import {
  Alert,
  Badge,
  Button,
  Skeleton,
  TruncatedText,
} from '@commenda/kakashi';
import { ReactNode, useState } from 'react';

import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { ServiceAuditFailureHelper } from '../helpers/ServiceAuditFailureHelper';
import { ServiceAuditReportHelper } from '../helpers/ServiceAuditReportHelper';
import {
  ServiceAuditCardState,
  ServiceAuditRunStatusHelper,
} from '../helpers/ServiceAuditRunStatusHelper';
import { useServiceAuditCard } from '../hooks/useServiceAuditCard';
import { ServiceAuditReportSheet } from './ServiceAuditReportSheet';
import { ServiceAuditRunStageList } from './ServiceAuditRunStageList';
import { ServiceAuditTaskStatusIcon } from './ServiceAuditTaskStatusIcon';

/** Service auditor step: reviewers watch the audit run; every other role sees only a neutral status. */
export const WorkflowServiceAuditTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, workflowExecution } = context;
  const [isReportSheetOpen, setIsReportSheetOpen] = useState(false);
  const {
    isAuditDetailShown,
    cardState,
    report,
    runStageRows,
    clientStatusLabel,
    auditStatus,
  } = useServiceAuditCard({ task, context });
  const statusBadge = ServiceAuditRunStatusHelper.getStatusBadge(cardState);
  const draftReadCoverageLine =
    ServiceAuditReportHelper.getDraftReadCoverageLine(
      report?.draftReadCoverage ?? null
    );
  const draftReadCoverageWarning =
    ServiceAuditReportHelper.getDraftReadCoverageWarning(
      report?.draftReadCoverage ?? null
    );

  const auditDetailByState: Record<ServiceAuditCardState, ReactNode> = {
    [ServiceAuditCardState.LOADING]: <Skeleton className="h-5 w-full" />,
    [ServiceAuditCardState.LOAD_ERROR]: (
      <p className="txt-small-regular text-text-error">
        {ServiceAuditRunStatusHelper.REPORT_LOAD_ERROR}
      </p>
    ),
    [ServiceAuditCardState.WAITING_FOR_DRAFT]: null,
    [ServiceAuditCardState.QUEUED]: (
      <>
        <p className="txt-extra-small-regular text-text-tertiary">
          {ServiceAuditRunStatusHelper.QUEUED_DETAIL}
        </p>
        <ServiceAuditRunStageList runStageRows={runStageRows} />
      </>
    ),
    [ServiceAuditCardState.RUNNING]: (
      <ServiceAuditRunStageList runStageRows={runStageRows} />
    ),
    [ServiceAuditCardState.COMPLETED]: report && (
      <div className="flex min-w-0 flex-col gap-xs">
        <p className="txt-small-regular text-text-secondary">
          {ServiceAuditReportHelper.getSummarySentence(
            report.counts,
            ServiceAuditReportHelper.getUniqueChecksPassed(report).length
          )}
        </p>
        {report.subject && (
          <TruncatedText className="txt-extra-small-regular text-text-tertiary">
            {report.subject.fileName}
          </TruncatedText>
        )}
        {draftReadCoverageLine && (
          <p className="txt-extra-small-regular text-text-tertiary">
            {draftReadCoverageLine}
          </p>
        )}
        {draftReadCoverageWarning && (
          <p className="txt-extra-small-regular text-text-warning">
            {draftReadCoverageWarning}
          </p>
        )}
      </div>
    ),
    [ServiceAuditCardState.FAILED]: report && (
      <>
        <Alert
          variant="destructive"
          title={ServiceAuditFailureHelper.FAILED_TITLE}
          description={ServiceAuditFailureHelper.getFailedMessage(report)}
        />
        <ServiceAuditRunStageList runStageRows={runStageRows} />
      </>
    ),
  };

  const label: ReactNode = isAuditDetailShown ? (
    <ServiceRequestTaskLabel label={task.title}>
      <div className="mt-md flex flex-col gap-md">
        {statusBadge && (
          <Badge
            size="sm"
            variant={statusBadge.variant}
            hasDot={statusBadge.hasDot}
          >
            {statusBadge.label}
          </Badge>
        )}
        {auditDetailByState[cardState]}
      </div>
    </ServiceRequestTaskLabel>
  ) : (
    <ServiceRequestTaskLabel label={task.title}>
      <p className="txt-extra-small-regular text-text-tertiary">
        {clientStatusLabel}
      </p>
    </ServiceRequestTaskLabel>
  );

  const actions: ReactNode =
    isAuditDetailShown && cardState === ServiceAuditCardState.COMPLETED ? (
      <Button size="sm" onClick={() => setIsReportSheetOpen(true)}>
        {ServiceAuditReportHelper.OPEN_REPORT_LABEL}
      </Button>
    ) : null;

  const config: ServiceRequestTaskCardConfig = {
    // Reviewers see the audit's own state; every other role sees the plain task status.
    getLeftIcon: (status) => (
      <ServiceAuditTaskStatusIcon
        auditStatus={isAuditDetailShown ? auditStatus : null}
        taskStatus={status}
      />
    ),
    getStateConfig: () => ({ label, actions }),
  };

  return (
    <>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      {isAuditDetailShown && (
        <ServiceAuditReportSheet
          isOpen={isReportSheetOpen}
          onClose={() => setIsReportSheetOpen(false)}
          role={role}
          workflowExecutionId={workflowExecution?.id}
          auditorStepId={task.workflowDefinitionStepId}
        />
      )}
    </>
  );
};
